import Phaser from 'phaser';
import { EffectId } from '@game/protocol';

import { addTexture, context2d, createCanvas, ellipse, px } from './canvas.ts';

/**
 * Texturas de efeito.
 *
 * Cada efeito é **uma** imagem; o movimento vem de escala, giro e transparência
 * aplicados no `render/effects.ts`. Animar por quadros exigiria de quatro a oito
 * desenhos por efeito para um resultado que o olho não distingue num evento que dura
 * 300ms.
 */

const SIZE = 24;

export function effectTextureKey(effectId: number): string {
  return `effect-${effectId}`;
}

export function buildEffectTextures(scene: Phaser.Scene): void {
  const painters: Array<[number, (ctx: CanvasRenderingContext2D) => void]> = [
    [EffectId.Hit, (ctx) => burst(ctx, '#f2f2f2', '#d94f4f')],
    [EffectId.Fire, (ctx) => burst(ctx, '#ffd166', '#e04a20')],
    [EffectId.Ice, (ctx) => shards(ctx, '#bfe9ff', '#4fa3d9')],
    [EffectId.Energy, (ctx) => bolt(ctx, '#d6c6ff', '#7f5af0')],
    [EffectId.Heal, (ctx) => cross(ctx, '#b7f5c4', '#3fa85e')],
    [EffectId.Roots, (ctx) => roots(ctx, '#8fae57', '#4a6d43')],
    [EffectId.Shout, (ctx) => ring(ctx, '#ffe9a8')],
    [EffectId.Charge, (ctx) => ring(ctx, '#cfd6de')],
    [EffectId.Arrow, (ctx) => arrow(ctx, '#e8e2cf', '#8a6a44')],
    [EffectId.MagicArrow, (ctx) => arrow(ctx, '#d6c6ff', '#7f5af0')],
    [EffectId.Fireball, (ctx) => burst(ctx, '#ffd166', '#e04a20')],
    // A bola de gelo é a mesma silhueta da de fogo em outra temperatura, e não os
    // estilhaços do `Ice`: projétil precisa ser legível **em movimento**, e uma estrela de
    // seis pontas girando pela tela vira um borrão. O que separa os dois é a cor, que é
    // exatamente o que o jogador tem tempo de ler em 200ms de voo.
    [EffectId.Iceball, (ctx) => burst(ctx, '#eaf8ff', '#3d84c6')],
  ];

  for (const [id, paint] of painters) {
    const key = effectTextureKey(id);
    if (scene.textures.exists(key)) continue;

    const canvas = createCanvas(SIZE, SIZE);
    paint(context2d(canvas));
    addTexture(scene, key, canvas);
  }
}

const C = SIZE / 2;

function burst(ctx: CanvasRenderingContext2D, inner: string, outer: string): void {
  ctx.globalAlpha = 0.55;
  ellipse(ctx, C, C, 11, 11, outer);
  ctx.globalAlpha = 1;
  ellipse(ctx, C, C, 7, 7, outer);
  ellipse(ctx, C, C, 4, 4, inner);
}

function shards(ctx: CanvasRenderingContext2D, inner: string, outer: string): void {
  for (const angle of [0, 60, 120, 180, 240, 300]) {
    const radians = (angle * Math.PI) / 180;
    ctx.save();
    ctx.translate(C, C);
    ctx.rotate(radians);
    px(ctx, -1, -10, 3, 9, outer);
    px(ctx, -1, -10, 3, 4, inner);
    ctx.restore();
  }
  ellipse(ctx, C, C, 3, 3, inner);
}

function bolt(ctx: CanvasRenderingContext2D, inner: string, outer: string): void {
  ctx.strokeStyle = outer;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(C - 6, 2);
  ctx.lineTo(C + 2, C - 2);
  ctx.lineTo(C - 3, C + 2);
  ctx.lineTo(C + 6, SIZE - 2);
  ctx.stroke();

  ctx.strokeStyle = inner;
  ctx.lineWidth = 1;
  ctx.stroke();
}

function cross(ctx: CanvasRenderingContext2D, inner: string, outer: string): void {
  ctx.globalAlpha = 0.5;
  ellipse(ctx, C, C, 10, 10, outer);
  ctx.globalAlpha = 1;
  px(ctx, C - 2, C - 8, 5, 16, outer);
  px(ctx, C - 8, C - 2, 16, 5, outer);
  px(ctx, C - 1, C - 7, 3, 14, inner);
  px(ctx, C - 7, C - 1, 14, 3, inner);
}

function roots(ctx: CanvasRenderingContext2D, inner: string, outer: string): void {
  for (const offset of [-7, -2, 3, 8]) {
    px(ctx, C + offset, C - 2, 2, 12, outer);
    px(ctx, C + offset, C - 6, 2, 5, inner);
  }
  px(ctx, 2, C + 8, SIZE - 4, 2, outer);
}

function ring(ctx: CanvasRenderingContext2D, color: string): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(C, C, 9, 0, Math.PI * 2);
  ctx.stroke();

  ctx.globalAlpha = 0.5;
  ctx.beginPath();
  ctx.arc(C, C, 5, 0, Math.PI * 2);
  ctx.stroke();
}

/**
 * Projétil apontando para a **direita**.
 *
 * A direção é dada girando o sprite no jogo, e não desenhando um quadro por ângulo: o
 * ângulo de um tiro é contínuo, e uma tabela de quadros nunca cobriria todos.
 */
function arrow(ctx: CanvasRenderingContext2D, head: string, shaft: string): void {
  px(ctx, C - 8, C - 1, 12, 2, shaft);
  ctx.fillStyle = head;
  ctx.beginPath();
  ctx.moveTo(C + 9, C);
  ctx.lineTo(C + 3, C - 4);
  ctx.lineTo(C + 3, C + 4);
  ctx.closePath();
  ctx.fill();
  px(ctx, C - 9, C - 3, 2, 6, head);
}
